import axios from 'axios';

export interface HomeEventSummary {
  id: number;
  event_name: string;
  event_date: string | null;
}

interface HomeEventsResponse {
  success?: boolean;
  events?: unknown;
  message?: string;
}

const isHomeEventSummary = (value: unknown): value is HomeEventSummary => {
  if (typeof value !== 'object' || value === null) return false;
  const event = value as Record<string, unknown>;
  return (
    typeof event.id === 'number' &&
    typeof event.event_name === 'string' &&
    (event.event_date === null || typeof event.event_date === 'string')
  );
};

const eventTimestamp = (event: HomeEventSummary): number => {
  if (!event.event_date) return Number.NEGATIVE_INFINITY;
  const timestamp = new Date(event.event_date).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

export const fetchHomeEvents = async (
  boatId: number,
  signal?: AbortSignal,
): Promise<HomeEventSummary[]> => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem('token');

  if (!apiBaseUrl) {
    throw new Error('Home events endpoint is not configured.');
  }
  if (!token) {
    throw new Error('Missing authentication token.');
  }

  const response = await axios.post<HomeEventsResponse>(
    `${apiBaseUrl}/api/user/getEvents`,
    { boat_id: boatId },
    { headers: { Authorization: `Bearer ${token}` }, signal },
  );

  if (!response.data.success || !Array.isArray(response.data.events)) {
    throw new Error(response.data.message ?? 'Could not load recent sessions.');
  }

  return response.data.events
    .filter((event): event is HomeEventSummary => isHomeEventSummary(event))
    .sort((first, second) => {
      const dateDifference = eventTimestamp(second) - eventTimestamp(first);
      return dateDifference === 0
        ? first.event_name.localeCompare(second.event_name)
        : dateDifference;
    });
};
